import type { LibraryLocation } from '@modules/search/type/hits';
import type { SearchLocation } from '../type';

export const getRelevantPhysicalLocation = (
    locations?: LibraryLocation[],
    appliedLocationFilter?: SearchLocation,
): string => {
    if (!appliedLocationFilter || !locations || !locations.length) {
        return '';
    }

    let relevantLocation = getMatchLocation(
        locations,
        appliedLocationFilter,
        true,
    );

    // no match found, use the first one
    if (!relevantLocation) {
        relevantLocation = locations[0];
    }

    if (!relevantLocation) {
        return '';
    }

    // create location string
    const { Suburb, District, City, Region, State } = relevantLocation;

    // remove undefined and empty value and make first letter capitalize
    const filteredLocationStr = [Suburb, District, City, Region, State]
        .filter((value) => !!value)
        .map((value) => value!.trim());

    // remove duplicate values
    return [...new Set(filteredLocationStr)].slice(0, 2).join(', ');
};

export const getRelevantServicingArea = (
    locations?: LibraryLocation[],
    appliedLocationFilter?: SearchLocation,
): string => {
    if (!appliedLocationFilter || !locations || !locations.length) {
        return '';
    }

    const relevantLocation = getMatchLocation(
        locations,
        appliedLocationFilter,
        false,
    );

    if (!relevantLocation) {
        return '';
    }

    // create location string
    const { Suburb, District, City, Region, State, Country } = relevantLocation;

    // remove undefined and empty value and make first letter capitalize
    const filteredLocationStr = [Suburb, District, City, Region, State, Country]
        .filter((value) => !!value)
        .map((value) => value!.trim());

    const hasOtherAreas = locations.length > 1;
    // remove duplicate values
    const distinctLocationStr = [...new Set(filteredLocationStr)]
        .slice(0, 1)
        .join(', ');
    const otherLocations = hasOtherAreas
        ? ` + ${locations.length - 1} other area${
              locations.length > 2 ? 's' : ''
          }`
        : '';

    return `${distinctLocationStr}${otherLocations}`;
};

export const getMatchLocation = (
    locations?: LibraryLocation[],
    searchLocation?: SearchLocation,
    eagerMatch?: boolean,
): LibraryLocation | undefined => {
    if (!locations || !locations.length || !searchLocation) {
        return undefined;
    }

    const {
        suburb: searchSuburb = '',
        district: searchDistrict = '',
        city: searchCity = '',
        region: searchRegion = '',
        state: searchState = '',
        country: searchCountry = '',
    } = searchLocation;

    if (
        !searchSuburb &&
        !searchDistrict &&
        !searchCity &&
        !searchRegion &&
        !searchState &&
        !searchCountry
    ) {
        return undefined;
    }

    let relevantLocation: LibraryLocation | undefined = undefined;

    // find 100% match location
    relevantLocation = locations.find(
        ({ Suburb, District, City, Region, State }) => {
            return (
                searchSuburb === Suburb?.trim() &&
                searchDistrict === District?.trim() &&
                searchCity === City?.trim() &&
                searchRegion === Region?.trim() &&
                searchState === State?.trim()
            );
        },
    );

    if (searchDistrict && !relevantLocation) {
        // find district level match location
        relevantLocation = locations.find(
            ({ Suburb, District, City, Region, State }) => {
                return (
                    (!Suburb?.trim() || eagerMatch) &&
                    searchDistrict === District?.trim() &&
                    searchCity === City?.trim() &&
                    searchRegion === Region?.trim() &&
                    searchState === State?.trim()
                );
            },
        );
    }

    if (searchCity && !relevantLocation) {
        // find city level match location
        relevantLocation = locations.find(
            ({ Suburb, District, City, Region, State }) => {
                return (
                    ((!Suburb?.trim() && !District?.trim()) || eagerMatch) &&
                    searchCity === City?.trim() &&
                    searchRegion === Region?.trim() &&
                    searchState === State?.trim()
                );
            },
        );
    }

    if (searchRegion && !relevantLocation) {
        // find region level match location
        relevantLocation = locations.find(
            ({ Suburb, District, City, Region, State }) => {
                return (
                    ((!Suburb?.trim() && !District?.trim() && !City?.trim()) ||
                        eagerMatch) &&
                    searchRegion === Region?.trim() &&
                    searchState === State?.trim()
                );
            },
        );
    }

    if (searchState && !relevantLocation) {
        // find state level match location
        relevantLocation = locations.find(
            ({ Suburb, District, City, Region, State }) => {
                return (
                    ((!Suburb?.trim() &&
                        !District?.trim() &&
                        !City?.trim() &&
                        !Region?.trim()) ||
                        eagerMatch) &&
                    searchState === State?.trim()
                );
            },
        );
    }

    if (!relevantLocation) {
        // find country level match location
        relevantLocation = locations.find(
            ({ Suburb, District, City, Region, State }) => {
                return (
                    !Suburb?.trim() &&
                    !District?.trim() &&
                    !City?.trim() &&
                    !Region?.trim() &&
                    !State?.trim()
                );
            },
        );
    }

    return relevantLocation;
};
